import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  getStepsByAgentId,
  defaultSteps,
  getAgentStartedSteps,
} from "../../utils/constants";
import "../../pages/Dashboard.scss";

export type ProgressStatus = "pending" | "active" | "completed";

export interface ProgressStep {
  id: string;
  title: string;
  status: ProgressStatus;
}

interface StreamProgressTrackerProps {
  isImageGeneration?: boolean;
}

export default function StreamProgressTracker({ isImageGeneration }: StreamProgressTrackerProps = {}) {
  const { isStreaming, streamProgress } = useSelector(
    (state: RootState) => state.user
  );
  const [steps, setSteps] = useState<ProgressStep[]>([]);
  const processedAgentsRef = useRef<Set<string>>(new Set());
  const progressIntervalRef = useRef<number | null>(null);
  const currentStepIndexRef = useRef<number>(0);
  const allStepsRef = useRef<ProgressStep[]>([]);
  const isStreamingRef = useRef<boolean>(false);

  // Initialize with default "Thinking" step when streaming starts
  useEffect(() => {
    if (isStreaming && streamProgress.length === 0) {
      const thinkingStep = {
        id: "thinking",
        title: "Thinking",
        status: "active" as ProgressStatus,
      };

      setSteps([thinkingStep]);
      processedAgentsRef.current = new Set();
      allStepsRef.current = [thinkingStep];
      currentStepIndexRef.current = 0;
      isStreamingRef.current = true;

      // Clear any existing interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    } else if (!isStreaming) {
      isStreamingRef.current = false;
    }
  }, [isStreaming, streamProgress.length]);

  // Handle stream progress updates - APPEND agent-specific steps
  useEffect(() => {
    if (!isStreaming || streamProgress.length === 0) return;

    const latestProgress = streamProgress[streamProgress.length - 1];
    const agentId = latestProgress.executorId;

    // Skip if we've already appended steps for this agent
    if (processedAgentsRef.current.has(agentId)) {
      return;
    }

    // Special handling for contract_reviewer_qa_agent: clear all previous steps and show only "Thinking"
    if (agentId === "contract_reviewer_qa_agent") {

      // Get QA steps (just "Thinking")
      const agentSteps = getAgentStartedSteps(agentId, isImageGeneration);

      if (agentSteps.length > 0) {
        // Clear all previous tracking
        processedAgentsRef.current.clear();
        processedAgentsRef.current.add(agentId);

        // Prepare "Thinking" step as pending
        const newSteps = agentSteps.map((step) => ({
          ...step,
          status: "pending" as ProgressStatus,
        }));

        // Replace all steps with just "Thinking"
        allStepsRef.current = newSteps;
        currentStepIndexRef.current = 0;

        // Update display immediately
        setSteps(
          allStepsRef.current.map((step, index) => ({
            ...step,
            status: index === 0 ? ("active" as ProgressStatus) : ("pending" as ProgressStatus),
          }))
        );
      }
      return;
    }

    // Get steps for this agent from constants.ts (ONLY source of steps)
    const agentSteps = getAgentStartedSteps(agentId, isImageGeneration);

    // IMPORTANT: Only show steps if explicitly defined in constants.ts
    // Do NOT create generic/random steps for unknown agents
    if (agentSteps.length === 0) {
      return;
    }

    // Mark this agent as processed
    processedAgentsRef.current.add(agentId);

    // Mark all previous steps as completed
    const completedPrevSteps = allStepsRef.current.map((s) => ({
      ...s,
      status: "completed" as ProgressStatus,
    }));

    // Prepare new steps as pending
    const newSteps = agentSteps.map((step) => ({
      ...step,
      status: "pending" as ProgressStatus,
    }));

    // Combine: previous (completed) + new (pending)
    allStepsRef.current = [...completedPrevSteps, ...newSteps];

    // Move current step index to first new step (right after completed ones)
    currentStepIndexRef.current = completedPrevSteps.length;

    // Update display immediately with first new step as active
    setSteps(
      allStepsRef.current.map((step, index) => ({
        ...step,
        status:
          index < currentStepIndexRef.current
            ? ("completed" as ProgressStatus)
            : index === currentStepIndexRef.current
              ? ("active" as ProgressStatus)
              : ("pending" as ProgressStatus),
      }))
    );
  }, [isStreaming, streamProgress, isImageGeneration]);

  // Continuous interval to progress through steps every 1 second while streaming
  useEffect(() => {
    if (!isStreaming) return;

    // Start continuous interval that runs every 1 second
    progressIntervalRef.current = window.setInterval(() => {
      if (currentStepIndexRef.current < allStepsRef.current.length - 1) {
        // Move to next step
        currentStepIndexRef.current += 1;
      }

      // Update display with current active step
      setSteps((prevSteps) =>
        prevSteps.map((step, index) => ({
          ...step,
          status:
            index < currentStepIndexRef.current
              ? ("completed" as ProgressStatus)
              : index === currentStepIndexRef.current
                ? ("active" as ProgressStatus)
                : ("pending" as ProgressStatus),
        }))
      );
    }, 1000); // Progress every 1 second

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isStreaming]);

  // Mark all steps as completed when streaming ends
  useEffect(() => {
    if (!isStreaming && steps.length > 0) {
      // Clear any running interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      setSteps((prevSteps) =>
        prevSteps.map((step) => ({
          ...step,
          status: "completed" as ProgressStatus,
        }))
      );
    }
  }, [isStreaming]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  if (!isStreaming && steps.length === 0) {
    return null;
  }

  return (
    <div className="chat-progress-tracker">
      {steps.map((step) => (
        <div
          key={step.id}
          className={`progress-step ${step.status}`}
          style={{ animationDelay: "0s" }}
        >
          <div className="progress-step-icon">
            <div
              className={`progress-circle ${step.status} ${
                step.status === "active" ? "rotating" : ""
              }`}
            />
          </div>

          <div className="progress-step-content">
            <div className="progress-step-title">{step.title}</div>
            <div className="progress-step-status">
              {step.status === "completed"
                ? "Completed"
                : step.status === "active"
                  ? "In progress"
                  : "Pending"}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
